import { useState } from 'react';
import { Alert, Button, Paper, TextField, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addRequest } from '../../api/requests';
import { logout as logoutRequest } from '../../api/auth';
import { USER_STORAGE_KEY } from '../../api/client';
import { logout } from '../../store/user.slice';
import { EventSelector } from '../../components/EventSelector';
import { TypeSelector } from '../../components/TypeSelector';
import { useUserData } from '../../hooks/useUserData';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export function RequestFormPage() {
    useDocumentTitle('Nuova richiesta');
    const dispatch = useDispatch();
    const user = useUserData();
    const navigate = useNavigate();

    const [event, setEvent] = useState<number | ''>('');
    const [title, setTitle] = useState('');
    const [author, setAuthor] = useState('');
    const [dedication, setDedication] = useState('');
    const [tono, setTono] = useState('');
    // Il tipo di default arriva dalla lista (TypeSelector), non da un id fisso.
    const [type, setType] = useState<number | ''>('');

    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const canSubmit =
        event !== '' && title.trim().length > 0 && author.trim().length > 0 && type !== '' && !submitting;

    const handleSubmit = async () => {
        if (!user.logged) return;
        setSubmitting(true);
        setError(null);
        setSuccess(false);
        try {
            await addRequest({
                event: Number(event),
                title: title.trim(),
                author: author.trim(),
                dedication: dedication.trim() || undefined,
                tono: tono.trim() || undefined,
                type: Number(type),
            });
            setTitle('');
            setAuthor('');
            setDedication('');
            setTono('');
            setSuccess(true);
        } catch (err) {
            // Token scaduto o non valido (401): esci e torna al login invece di
            // lasciare l'utente bloccato sul form con un errore generico.
            const status = (err as { response?: { status?: number } }).response?.status;
            if (status === 401) {
                localStorage.removeItem(USER_STORAGE_KEY);
                dispatch(logout());
                // M4: azzera anche il cookie di autenticazione lato server.
                void logoutRequest().catch(() => undefined);
                navigate('/');
                return;
            }
            setError("Errore durante l'invio. Riprova.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex justify-center">
            <Paper elevation={0} className="w-full max-w-xl border border-white/10 bg-black/40 p-8">
                <Typography variant="h4" sx={{ fontWeight: 900 }} className="mb-1">
                    Benvenuto <span className="text-accent">{user.username}</span>
                </Typography>
                <Typography variant="body2" color="text.secondary" className="mb-6">
                    Compila i campi per inviare la tua richiesta
                </Typography>

                <div className="flex flex-col gap-4">
                    {success && (
                        <Alert severity="success" onClose={() => setSuccess(false)}>
                            Aggiunto con successo
                        </Alert>
                    )}
                    {error && <Alert severity="error">{error}</Alert>}

                    <EventSelector label="In che locale ti trovi?" required value={event} onChange={setEvent} />

                    <TextField
                        label="Titolo"
                        required
                        fullWidth
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                    <TextField
                        label="Autore"
                        required
                        fullWidth
                        value={author}
                        onChange={(e) => setAuthor(e.target.value)}
                    />
                    <TextField
                        label="Dedica"
                        fullWidth
                        value={dedication}
                        onChange={(e) => setDedication(e.target.value)}
                    />
                    <TextField label="Tonalità" fullWidth value={tono} onChange={(e) => setTono(e.target.value)} />
                    <TypeSelector label="Cosa vuoi fare?" value={type} onChange={setType} />

                    <Button variant="contained" size="large" disabled={!canSubmit} onClick={handleSubmit}>
                        {submitting ? 'Invio...' : 'Invia'}
                    </Button>
                    <Button variant="text" onClick={() => navigate('/')}>
                        Torna alla home
                    </Button>
                </div>
            </Paper>
        </div>
    );
}
